import { Amplify } from 'aws-amplify';
import type { ResourcesConfig } from 'aws-amplify';
import type { SignInInput } from 'aws-amplify/auth';
import { environment } from '../../environments/environment';

export type AuthType = 'saml' | 'native';

type AuthFlowType = NonNullable<NonNullable<SignInInput['options']>['authFlowType']>;

interface LegacyAuthConfig {
    region: string;
    userPoolId: string;
    userPoolWebClientId: string;
    authenticationFlowType?: string;
    oauth: {
        domain: string;
        scope: string[];
        redirectSignIn: string;
        redirectSignOut: string;
        responseType: string;
    };
}

const DEFAULT_AUTH_FLOW_TYPE: AuthFlowType = 'USER_SRP_AUTH';

function legacyConfigFor(authType: AuthType): LegacyAuthConfig {
    return (authType === 'saml'
        ? environment.amplifyConfig.samlAuth
        : environment.amplifyConfig.nativeAuth) as unknown as LegacyAuthConfig;
}

function buildRedirectUrls(configuredUrl: string): string[] {
    if (environment.production) {
        return [configuredUrl];
    }

    try {
        const configured = new URL(configuredUrl);
        const currentOrigin = window.location.origin;

        if (configured.origin === currentOrigin) {
            return [configuredUrl];
        }

        const sameOriginUrl = `${currentOrigin}${configured.pathname}${configured.search}${configured.hash}`;
        return [configuredUrl, sameOriginUrl];
    } catch {
        return [configuredUrl];
    }
}

function toResourcesConfig(config: LegacyAuthConfig, authType: AuthType): ResourcesConfig {
    const oauth: NonNullable<NonNullable<ResourcesConfig['Auth']>['Cognito']['loginWith']>['oauth'] = {
        domain: config.oauth.domain,
        scopes: config.oauth.scope,
        redirectSignIn: buildRedirectUrls(config.oauth.redirectSignIn),
        redirectSignOut: buildRedirectUrls(config.oauth.redirectSignOut),
        responseType: (config.oauth.responseType === 'token' ? 'token' : 'code'),
    };

    if (authType === 'saml' && environment.samlProviderName) {
        oauth.providers = [{ custom: environment.samlProviderName }];
    }

    return {
        Auth: {
            Cognito: {
                userPoolId: config.userPoolId,
                // v5: userPoolWebClientId
                userPoolClientId: config.userPoolWebClientId,
                loginWith: {
                    email: true,
                    username: true,
                    oauth,
                },
            },
        },
    };
}

export function getAuthFlowType(authType: AuthType): AuthFlowType {
    const flowType = legacyConfigFor(authType).authenticationFlowType;
    return (flowType as AuthFlowType) || DEFAULT_AUTH_FLOW_TYPE;
}

export function configureAmplify(authType: AuthType): void {
    const config = legacyConfigFor(authType);

    if (!config?.userPoolId || !config?.userPoolWebClientId) {
        console.error(`Amplify: configurazione "${authType}" incompleta (userPoolId/userPoolWebClientId mancanti).`);
        return;
    }

    try {
        Amplify.configure(toResourcesConfig(config, authType));
    } catch (error) {
        console.error('Errore durante Amplify.configure:', error);
    }
}

export function resolveInitialAuthType(storedAuthType: string | null): AuthType {
    if (!environment.samlEnabled) {
        return 'native';
    }

    if (window.location.pathname === '/login-admin') {
        return 'native';
    }

    const params = new URLSearchParams(window.location.search);
    if (params.has('code') || params.has('error')) {
        return 'saml';
    }

    return storedAuthType === 'native' ? 'native' : 'saml';
}
